import React, { useState, useContext } from 'react';
import { Flag } from 'lucide-react';
import { SafeDatabaseUser } from '../../../../types/types';
import { Card, CardContent } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import ReportUserModal from '../../reportUserModal';
import UserContext from '../../../../contexts/UserContext';

/**
 * Interface representing the props for the User component.
 *
 * user - The user object containing details about the user.
 * handleUserCardViewClickHandler - The function to handle the click event on the user card.
 */
interface UserProps {
  user: SafeDatabaseUser;
  handleUserCardViewClickHandler: (user: SafeDatabaseUser) => void;
}

/**
 * User component renders the details of a user including its username and dateJoined.
 * Clicking on the component triggers the handleUserPage function,
 * and clicking on a tag triggers the clickTag function.
 *
 * @param user - The user object containing user details.
 */
const UserCardView = (props: UserProps) => {
  const { user, handleUserCardViewClickHandler } = props;
  const context = useContext(UserContext);
  const currentUser = context ? context.user : null;
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Use type assertion to avoid TypeScript errors
  const userWithExtras = user as SafeDatabaseUser & { followers?: string[]; rank?: number };
  const followerCount = userWithExtras.followers ? userWithExtras.followers.length : 0;

  // Calculate the rank level based on points (rank field now stores points)
  const pointsTotal = userWithExtras.rank || 0;
  const rankLevel = Math.floor(pointsTotal / 15) + 1;

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent card click when clicking the report button
    if ((e.target as HTMLElement).closest('.report-button')) {
      e.stopPropagation();
      return;
    }
    handleUserCardViewClickHandler(user);
  };

  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsReportModalOpen(true);
  };

  // Don't show report button if viewing own profile or not logged in
  const showReportButton = currentUser && currentUser.username !== user.username;

  return (
    <>
      <Card
        className='cursor-pointer hover:shadow-md transition-shadow border-t border-dashed border-gray-300'
        onClick={handleCardClick}
        role='button'
        aria-label={`View ${user.username}'s profile`}>
        <CardContent className='p-4 flex justify-between items-center'>
          <div className='ml-2'>
            <div className='text-blue-500 font-medium hover:underline'>{user.username}</div>
            {followerCount > 0 && (
              <div className='text-sm text-gray-500'>
                <Badge variant='outline'>{followerCount} followers</Badge>
              </div>
            )}
          </div>
          <div className='flex items-center gap-3'>
            <div className='text-gray-400 text-sm text-right'>
              <div>joined {new Date(user.dateJoined).toLocaleDateString()}</div>
              {userWithExtras.rank !== undefined && (
                <div className='text-sm text-gray-500'>
                  Level: {rankLevel} ({pointsTotal} points)
                </div>
              )}
            </div>
            {showReportButton && (
              <Button
                variant='ghost'
                size='sm'
                className='report-button'
                onClick={handleReportClick}
                title='Report user'>
                <Flag className='h-4 w-4 text-red-500' />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {showReportButton && (
        <ReportUserModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          reportedUser={user}
          currentUser={currentUser}
        />
      )}
    </>
  );
};

export default UserCardView;
